<template>
  <v-navigation-drawer
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :temporary="isMobile"
    :permanent="!isMobile && modelValue"
    width="240"
    class="app-sidebar"
    color="surface"
  >
    <!-- Header con Logo -->
    <div class="sidebar-header pa-3">
      <div class="d-flex align-center" :class="{ 'justify-center': !isMobile && !isHovered }">
        <!-- Logo de marca de Facturación (siempre visible) -->
        <FacturacionLogo :size="38" class="logo-animate" />
        <transition name="fade-text">
          <div v-show="isMobile || isHovered" class="ml-2" style="white-space: nowrap; overflow: hidden;">
            <h2 class="text-subtitle-2 font-weight-bold text-primary">SMARTIX</h2>
            <p class="text-caption text-grey" style="font-size: 10px;">El Salvador</p>
          </div>
        </transition>
      </div>
    </div>

    <!-- Modal para cambiar logo del sistema (Solo SuperAdmin) -->
    <v-dialog v-model="modalLogo" max-width="400" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-image-edit</v-icon>
          Cambiar Logo del Sistema
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 text-grey mb-4">
            Este logo se mostrará a todos los usuarios del sistema.
          </p>

          <!-- Preview del logo -->
          <div class="text-center mb-4">
            <v-avatar size="100" color="grey-lighten-3" rounded="lg" class="elevation-2">
              <v-img
                v-if="logoPreview || systemLogoUrl"
                :src="logoPreview || systemLogoUrl"
                alt="Vista previa"
                cover
              />
              <v-icon v-else size="48" color="grey-lighten-1">mdi-image-plus</v-icon>
            </v-avatar>
          </div>

          <!-- Input de archivo -->
          <input
            ref="logoInputRef"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onLogoChange"
          />
          <v-btn
            block
            variant="outlined"
            color="primary"
            prepend-icon="mdi-upload"
            @click="$refs.logoInputRef.click()"
            class="mb-2"
          >
            Seleccionar imagen
          </v-btn>
          <p v-if="logoFile" class="text-caption text-grey text-center">
            {{ logoFile.name }} ({{ Math.round(logoFile.size / 1024) }} KB)
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cerrarModalLogo">Cancelar</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="subiendoLogo"
            :disabled="!logoFile"
            @click="guardarLogo"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="bottom"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <v-divider />

    <!-- Menú de Navegación -->
    <v-list nav density="comfortable" class="pa-2">
      <!-- Inicio - Siempre visible (cada rol tiene su dashboard personalizado) -->
      <v-list-item
        :to="{ name: 'dashboard' }"
        prepend-icon="mdi-home"
        title="Inicio"
        value="dashboard"
        rounded="lg"
        class="mb-1"
      />

      <!-- ==================== MENÚ EXCLUSIVO PARA SUPERADMIN ==================== -->
      <!-- SuperAdmin solo ve: Gestión Emisores y Usuarios -->
      <template v-if="esSuperAdmin">
        <v-list-item
          :to="{ name: 'configuracion-emisores' }"
          prepend-icon="mdi-store-cog"
          title="Gestión Emisores"
          value="emisores"
          rounded="lg"
          class="mb-1"
        />

        <v-list-item
          :to="{ name: 'configuracion-usuarios' }"
          prepend-icon="mdi-account-cog"
          title="Usuarios"
          value="usuarios"
          rounded="lg"
          class="mb-1"
        />

        <v-list-item
          :to="{ name: 'configuracion-suscripciones' }"
          prepend-icon="mdi-credit-card-outline"
          title="Suscripciones"
          value="suscripciones"
          rounded="lg"
          class="mb-1"
        />
      </template>

      <!-- ==================== MENÚ PARA OTROS ROLES ==================== -->
      <template v-else>
      <!-- Facturación Electrónica - Agrupa todo lo relacionado con DTEs -->
      <v-list-group v-if="puedeEmitirDTE || puedeVerHistorial || puedeVerPendientes || puedeVerLotes || puedeVerContingencia" value="facturacion">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-file-certificate"
            title="Facturación Electrónica"
            rounded="lg"
            class="mb-1"
          />
        </template>

        <!-- Emitir DTE - Submenú -->
        <v-list-group v-if="puedeEmitirDTE" value="emision">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-file-plus"
              title="Emitir DTE"
              rounded="lg"
              class="ml-2"
            />
          </template>

          <v-tooltip
            :disabled="credencialesProdConfiguradas"
            text="Configure las credenciales de producción del emisor para emitir DTEs"
            location="end"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-if="puedeCrearFactura"
                v-bind="tooltipProps"
                :to="credencialesProdConfiguradas ? { name: 'emision-factura' } : undefined"
                :disabled="!credencialesProdConfiguradas"
                prepend-icon="mdi-receipt-text"
                title="Factura"
                value="factura"
                rounded="lg"
                class="ml-4"
              />
            </template>
          </v-tooltip>

          <v-tooltip
            :disabled="credencialesProdConfiguradas"
            text="Configure las credenciales de producción del emisor para emitir DTEs"
            location="end"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-if="puedeCrearCreditoFiscal"
                v-bind="tooltipProps"
                :to="credencialesProdConfiguradas ? { name: 'emision-ccf' } : undefined"
                :disabled="!credencialesProdConfiguradas"
                prepend-icon="mdi-domain"
                title="Crédito Fiscal"
                value="ccf"
                rounded="lg"
                class="ml-4"
              />
            </template>
          </v-tooltip>

          <v-tooltip
            :disabled="credencialesProdConfiguradas"
            text="Configure las credenciales de producción del emisor para emitir DTEs"
            location="end"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-if="puedeCrearFSE"
                v-bind="tooltipProps"
                :to="credencialesProdConfiguradas ? { name: 'emision-fse' } : undefined"
                :disabled="!credencialesProdConfiguradas"
                prepend-icon="mdi-account-cash"
                title="Sujeto Excluido"
                value="fse"
                rounded="lg"
                class="ml-4"
              />
            </template>
          </v-tooltip>

          <!-- Nota de Crédito -->
          <v-tooltip
            :text="!credencialesProdConfiguradas ? 'Configure las credenciales de Hacienda primero' : ''"
            :disabled="credencialesProdConfiguradas"
            location="end"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-if="puedeCrearNotaCredito"
                v-bind="tooltipProps"
                :to="credencialesProdConfiguradas ? { name: 'emision-nota-credito' } : undefined"
                :disabled="!credencialesProdConfiguradas"
                prepend-icon="mdi-file-minus"
                title="Nota de Crédito"
                value="nota-credito"
                rounded="lg"
                class="ml-4"
              />
            </template>
          </v-tooltip>

          <!-- Nota de Débito -->
          <v-tooltip
            :text="!credencialesProdConfiguradas ? 'Configure las credenciales de Hacienda primero' : ''"
            :disabled="credencialesProdConfiguradas"
            location="end"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-if="puedeCrearNotaDebito"
                v-bind="tooltipProps"
                :to="credencialesProdConfiguradas ? { name: 'emision-nota-debito' } : undefined"
                :disabled="!credencialesProdConfiguradas"
                prepend-icon="mdi-file-plus-outline"
                title="Nota de Débito"
                value="nota-debito"
                rounded="lg"
                class="ml-4"
              />
            </template>
          </v-tooltip>
        </v-list-group>

        <!-- DTEs Emitidos -->
        <v-list-item
          v-if="puedeVerHistorial"
          to="/historial"
          prepend-icon="mdi-file-document-multiple"
          title="DTEs Emitidos"
          value="historial"
          rounded="lg"
          class="ml-2"
        />

        <!-- Cuentas por Cobrar -->
        <v-list-item
          v-if="puedeVerCxc"
          :to="{ name: 'cuentas-por-cobrar' }"
          prepend-icon="mdi-account-cash"
          title="Cuentas por Cobrar"
          value="cuentas-por-cobrar"
          rounded="lg"
          class="ml-2"
        />

        <!-- DTEs Pendientes de Envío -->
        <v-list-item
          v-if="puedeVerPendientes"
          :to="{ name: 'facturas-pendientes' }"
          prepend-icon="mdi-clock-alert"
          title="Pendientes de Envío"
          value="pendientes"
          rounded="lg"
          class="ml-2"
        />

        <!-- Evento de Contingencia -->
        <v-list-item
          v-if="puedeVerContingencia"
          :to="{ name: 'contingencia' }"
          prepend-icon="mdi-lightning-bolt"
          title="Evento de Contingencia"
          value="contingencia-evento"
          rounded="lg"
          class="ml-2"
        />

        <!-- Operaciones Especiales (Evento 17) -->
        <v-list-item
          v-if="puedeCrearContingencia"
          :to="{ name: 'evento-operaciones-especiales' }"
          prepend-icon="mdi-file-document-multiple"
          title="Operaciones Especiales"
          value="evento-operaciones-especiales"
          rounded="lg"
          class="ml-2"
        />

        <!-- Evento de Retorno (Evento 18) -->
        <v-list-item
          v-if="puedeCrearContingencia"
          :to="{ name: 'evento-retorno' }"
          prepend-icon="mdi-keyboard-return"
          title="Evento de Retorno"
          value="evento-retorno"
          rounded="lg"
          class="ml-2"
        />
      </v-list-group>

      <!-- Compras Externas -->
      <v-list-group v-if="puedeVerCompras" value="compras-group">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-cart"
            title="Compras"
            rounded="lg"
            class="mb-1"
          />
        </template>
        <v-list-item
          :to="{ name: 'compras' }"
          prepend-icon="mdi-cart-outline"
          title="Registro de Compras"
          value="compras"
          rounded="lg"
          class="ml-2"
        />
        <v-list-item
          :to="{ name: 'dtes-recibidos' }"
          prepend-icon="mdi-email-open"
          title="DTEs Recibidos"
          value="dtes-recibidos"
          rounded="lg"
          class="ml-2"
        />
      </v-list-group>

      <!-- Inventario -->
      <v-list-group v-if="puedeVerInventario || puedeVerProductos" value="inventario">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-warehouse"
            title="Inventario"
            rounded="lg"
            class="mb-1"
          />
        </template>

        <v-list-item
          v-if="puedeVerInventario && !inventarioExterno"
          :to="{ name: 'inventario' }"
          prepend-icon="mdi-view-dashboard"
          title="Dashboard"
          value="inventario-dashboard"
          rounded="lg"
          class="ml-2"
        />


        <v-list-item
          :to="{ name: 'inventario-stock' }"
          prepend-icon="mdi-package-variant"
          title="Stock"
          value="inventario-stock"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerInventario && !inventarioExterno"
          :to="{ name: 'inventario-movimientos' }"
          prepend-icon="mdi-swap-horizontal"
          title="Movimientos"
          value="inventario-movimientos"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerInventario && !inventarioExterno"
          :to="{ name: 'inventario-kardex' }"
          prepend-icon="mdi-clipboard-list"
          title="Historial de Producto"
          value="inventario-kardex"
          rounded="lg"
          class="ml-2"
        />

        <!-- Subgrupo: Catálogos -->
        <v-list-group value="catalogos-inventario">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-folder-cog"
              title="Catálogos"
              rounded="lg"
              class="ml-2"
            />
          </template>

          <v-list-item
            v-if="puedeVerProductos"
            :to="{ name: 'catalogo-productos' }"
            prepend-icon="mdi-package-variant"
            title="Productos"
            value="catalogo-productos"
            rounded="lg"
            class="ml-4"
          />

          <v-list-item
            v-if="puedeVerProductos"
            :to="{ name: 'catalogo-servicios' }"
            prepend-icon="mdi-account-wrench"
            title="Servicios"
            value="catalogo-servicios"
            rounded="lg"
            class="ml-4"
          />

          <!-- F2: activos fijos (subset de productos con tipoInventario=MobiliarioEquipo) -->
          <v-list-item
            v-if="puedeVerProductos"
            :to="{ name: 'activos-fijos' }"
            prepend-icon="mdi-desk"
            title="Activos Fijos"
            value="activos-fijos"
            rounded="lg"
            class="ml-4"
          />

          <v-list-item
            v-if="puedeVerCategorias"
            :to="{ name: 'categorias' }"
            prepend-icon="mdi-shape"
            title="Categorías"
            value="categorias"
            rounded="lg"
            class="ml-4"
          />

          <v-list-item
            v-if="puedeVerMarcas"
            :to="{ name: 'marcas' }"
            prepend-icon="mdi-tag-multiple"
            title="Marcas"
            value="marcas"
            rounded="lg"
            class="ml-4"
          />

          <v-list-item
            v-if="puedeVerTiposGasto"
            :to="{ name: 'tipos-gasto' }"
            prepend-icon="mdi-cash-register"
            title="Tipos de Gasto"
            value="tipos-gasto"
            rounded="lg"
            class="ml-4"
          />
        </v-list-group>
      </v-list-group>

      <!-- Reportes -->
      <v-list-item
        v-if="puedeVerReportes"
        :to="{ name: 'reportes' }"
        prepend-icon="mdi-chart-bar"
        title="Reportes"
        value="reportes"
        rounded="lg"
        class="mb-1"
      />

      <v-divider v-if="puedeAccederConfiguracion" class="my-3" />

      <!-- Configuraciones -->
      <v-list-group v-if="puedeAccederConfiguracion" value="entidades">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-cog"
            title="Configuraciones"
            rounded="lg"
            class="mb-1"
          />
        </template>

        <!-- === NEGOCIO === -->
        <v-list-subheader v-if="(puedeVerClientes || puedeVerProveedores || puedeVerSucursales) && (isMobile || isHovered)" class="ml-2 mt-1">
          Negocio
        </v-list-subheader>

        <v-list-item
          v-if="esEmisorAdmin || esGerenteSucursal"
          :to="{ name: 'configuracion-emisor' }"
          prepend-icon="mdi-store"
          title="Emisor"
          value="emisor"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerEmisores"
          :to="{ name: 'configuracion-emisores' }"
          prepend-icon="mdi-store-cog"
          title="Gestión Emisores"
          value="emisores"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerClientes"
          :to="{ name: 'receptores' }"
          prepend-icon="mdi-account-multiple"
          title="Clientes"
          value="receptores"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerSucursales"
          :to="{ name: 'sucursales' }"
          prepend-icon="mdi-office-building"
          title="Sucursales"
          value="sucursales"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerProveedores"
          :to="{ name: 'proveedores' }"
          prepend-icon="mdi-truck-delivery"
          title="Proveedores"
          value="proveedores"
          rounded="lg"
          class="ml-2"
        />

        <!-- === PUNTO DE VENTA === -->
        <v-list-subheader v-if="(puedeVerCajas || puedeVerVendedores) && (isMobile || isHovered)" class="ml-2 mt-2">
          Punto de Venta
        </v-list-subheader>

        <v-list-item
          v-if="puedeVerCajas"
          :to="{ name: 'cajas' }"
          prepend-icon="mdi-cash-register"
          title="Cajas"
          value="cajas"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerVendedores"
          :to="{ name: 'vendedores' }"
          prepend-icon="mdi-account-tie"
          title="Vendedores"
          value="vendedores"
          rounded="lg"
          class="ml-2"
        />

        <!-- === SISTEMA === -->
        <v-list-subheader v-if="(puedeVerUsuarios || puedeVerMiSuscripcion) && (isMobile || isHovered)" class="ml-2 mt-2">
          Sistema
        </v-list-subheader>

        <v-list-item
          v-if="puedeVerUsuarios"
          :to="{ name: 'configuracion-usuarios' }"
          prepend-icon="mdi-account-cog"
          title="Usuarios"
          value="usuarios"
          rounded="lg"
          class="ml-2"
        />

        <v-list-item
          v-if="puedeVerMiSuscripcion"
          :to="{ name: 'mi-suscripcion' }"
          prepend-icon="mdi-credit-card-outline"
          title="Mi Suscripción"
          value="mi-suscripcion"
          rounded="lg"
          class="ml-2"
        />
      </v-list-group>
      </template>

    </v-list>

    <!-- Footer del Sidebar -->
    <template v-slot:append>
      <v-divider />
      <div class="pa-3 d-flex flex-column align-center">
        <v-chip
          v-if="isMobile || isHovered"
          :color="ambiente === '00' ? 'warning' : 'success'"
          size="small"
          label
          class="w-100 justify-center"
        >
          <v-icon start size="14">
            {{ ambiente === '00' ? 'mdi-test-tube' : 'mdi-shield-check' }}
          </v-icon>
          {{ ambiente === '00' ? 'PRUEBAS' : 'PRODUCTIVO' }}
        </v-chip>
        <v-icon
          v-else
          :color="ambiente === '00' ? 'warning' : 'success'"
          size="20"
        >
          {{ ambiente === '00' ? 'mdi-test-tube' : 'mdi-shield-check' }}
        </v-icon>
        <p v-show="isMobile || isHovered" class="text-caption text-center text-grey mt-2">v1.0.0</p>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { computed, ref, reactive, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { usePermisos } from '@/composables/usePermisos'
import emisoresService from '@/services/emisores.service'
import FacturacionLogo from '@/components/brand/FacturacionLogo.vue'

export default {
  name: 'AppSidebar',
  components: { FacturacionLogo },
  props: {
    modelValue: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const route = useRoute()
    const windowWidth = ref(window.innerWidth)

    const isHovered = ref(true)
    const ambiente = computed(() => store.getters['auth/ambiente'])
    const emisorActual = computed(() => store.getters['emisor/emisorActual'])
    const inventarioExterno = computed(() => emisorActual.value?.inventarioExterno === true)
    const credencialesProdConfiguradas = computed(() => {
      if (ambiente.value !== '01') return true // En pruebas siempre habilitado
      const e = emisorActual.value
      return !!(e?.mhUsuarioProd && e?.mhLlavePublicaProd)
    })
    const isMobile = computed(() => windowWidth.value < 960)

    // Auto-cerrar sidebar en móvil al navegar
    watch(() => route.path, () => {
      if (isMobile.value && props.modelValue) {
        emit('update:modelValue', false)
      }
    })

    // Estado para el logo del sistema
    const systemLogoUrl = ref(null)
    const modalLogo = ref(false)
    const logoFile = ref(null)
    const logoPreview = ref(null)
    const subiendoLogo = ref(false)
    const logoInputRef = ref(null)
    const snackbar = reactive({
      show: false,
      message: '',
      color: 'success'
    })

    // Actualizar el favicon del navegador con la URL del logo
    const actualizarFavicon = (url) => {
      let link = document.querySelector("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = url
    }

    // Verificar si una imagen existe usando fetch HEAD (más eficiente y silencioso)
    const verificarImagenExiste = async (url) => {
      try {
        const response = await fetch(url, { method: 'HEAD' })
        return response.ok
      } catch {
        return false
      }
    }

    // Cargar logo del sistema al iniciar
    const cargarLogoSistema = async () => {
      try {
        const response = await emisoresService.getSystemLogo()
        if (response?.url) {
          const baseUrl = response.url
          const timestamp = `?t=${Date.now()}`

          // Primero intentar la URL base directamente (sin extensión)
          // El backend ahora almacena el logo en ruta fija "system/logo"
          const existeBase = await verificarImagenExiste(`${baseUrl}${timestamp}`)
          if (existeBase) {
            systemLogoUrl.value = `${baseUrl}${timestamp}`
            actualizarFavicon(`${baseUrl}${timestamp}`)
            return
          }

          // Fallback: probar con extensiones comunes (logos subidos con versión anterior)
          const extensiones = ['.png', '.jpg', '.jpeg', '.svg', '.webp']
          for (const ext of extensiones) {
            const urlConExtension = `${baseUrl}${ext}${timestamp}`
            const existe = await verificarImagenExiste(urlConExtension)
            if (existe) {
              systemLogoUrl.value = urlConExtension
              actualizarFavicon(urlConExtension)
              return
            }
          }

          systemLogoUrl.value = null
        }
      } catch {
        // Si no hay logo configurado, mantener null (mostrará el icono por defecto)
        systemLogoUrl.value = null
      }
    }

    // Abrir modal para cambiar logo
    const abrirModalLogo = () => {
      logoFile.value = null
      logoPreview.value = null
      modalLogo.value = true
    }

    // Cerrar modal
    const cerrarModalLogo = () => {
      modalLogo.value = false
      logoFile.value = null
      logoPreview.value = null
    }

    // Manejar cambio de archivo
    const onLogoChange = (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        snackbar.message = 'Solo se permiten archivos de imagen'
        snackbar.color = 'error'
        snackbar.show = true
        return
      }

      // Validar tamaño (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        snackbar.message = 'El archivo no debe superar 2MB'
        snackbar.color = 'error'
        snackbar.show = true
        return
      }

      logoFile.value = file

      // Crear preview
      const reader = new FileReader()
      reader.onload = (e) => {
        logoPreview.value = e.target.result
      }
      reader.readAsDataURL(file)
    }

    // Guardar nuevo logo del sistema
    const guardarLogo = async () => {
      if (!logoFile.value) return

      subiendoLogo.value = true
      try {
        const response = await emisoresService.uploadSystemLogo(logoFile.value)
        if (response?.url) {
          // Limpiar posible timestamp previo del backend y agregar uno nuevo
          const urlBase = response.url.split('?')[0]
          const nuevaUrl = `${urlBase}?t=${Date.now()}`
          systemLogoUrl.value = nuevaUrl
          actualizarFavicon(nuevaUrl)
        }
        snackbar.message = 'Logo del sistema actualizado correctamente'
        snackbar.color = 'success'
        snackbar.show = true
        cerrarModalLogo()
      } catch (error) {
        const mensaje = error.response?.data?.message || error.response?.data?.error || 'Error al subir el logo'
        snackbar.message = mensaje
        snackbar.color = 'error'
        snackbar.show = true
      } finally {
        subiendoLogo.value = false
      }
    }

    const {
      esSuperAdmin,
      puedeEmitirDTE,
      puedeCrearFactura,
      puedeCrearCreditoFiscal,
      puedeCrearFSE,
      puedeCrearNotaCredito,
      puedeCrearNotaDebito,
      puedeVerHistorial,
      puedeVerPendientes,
      puedeVerLotes,
      puedeVerContingencia,
      puedeCrearContingencia,
      puedeVerClientes,
      puedeVerProductos,
      puedeVerProveedores,
      puedeVerCompras,
      puedeVerInventario,
      puedeVerCategorias,
      puedeVerMarcas,
      puedeVerTiposGasto,
      puedeVerReportes,
      puedeAccederConfiguracion,
      puedeVerEmisores,
      puedeVerSucursales,
      puedeVerUsuarios,
      puedeVerCajas,
      puedeVerVendedores,
      esEmisorAdmin,
      esGerenteSucursal,
      puedeVerMiSuscripcion,
      puedeVerCxc
    } = usePermisos()

    const handleResize = () => {
      windowWidth.value = window.innerWidth
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize)
      // Cargar logo del sistema
      cargarLogoSistema()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      isHovered,
      ambiente,
      credencialesProdConfiguradas,
      inventarioExterno,
      isMobile,
      esSuperAdmin,
      puedeEmitirDTE,
      puedeCrearFactura,
      puedeCrearCreditoFiscal,
      puedeCrearFSE,
      puedeCrearNotaCredito,
      puedeCrearNotaDebito,
      puedeVerHistorial,
      puedeVerPendientes,
      puedeVerLotes,
      puedeVerContingencia,
      puedeCrearContingencia,
      puedeVerClientes,
      puedeVerProductos,
      puedeVerProveedores,
      puedeVerCompras,
      puedeVerInventario,
      puedeVerCategorias,
      puedeVerMarcas,
      puedeVerTiposGasto,
      puedeVerReportes,
      puedeAccederConfiguracion,
      puedeVerEmisores,
      puedeVerSucursales,
      puedeVerUsuarios,
      puedeVerCajas,
      puedeVerVendedores,
      esEmisorAdmin,
      esGerenteSucursal,
      puedeVerMiSuscripcion,
      puedeVerCxc,
      // Logo del sistema
      systemLogoUrl,
      modalLogo,
      logoFile,
      logoPreview,
      subiendoLogo,
      logoInputRef,
      snackbar,
      abrirModalLogo,
      cerrarModalLogo,
      onLogoChange,
      guardarLogo
    }
  }
}
</script>

<style scoped>
.app-sidebar {
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
  background: rgba(var(--v-theme-surface), 0.7) !important;
  backdrop-filter: blur(20px) saturate(1.2);
  -webkit-backdrop-filter: blur(20px) saturate(1.2);
}

.sidebar-header {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.03) 0%, rgba(var(--v-theme-primary), 0.08) 100%);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.1);
  position: relative;
  overflow: hidden;
}

.sidebar-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 200%;
  background: radial-gradient(circle, rgba(var(--v-theme-primary), 0.05) 0%, transparent 70%);
  pointer-events: none;
}

/* Transición para el texto del header */
.fade-text-enter-active,
.fade-text-leave-active {
  transition: opacity 0.2s ease;
}

.fade-text-enter-from,
.fade-text-leave-to {
  opacity: 0;
}

/* Estilos modernos para items del menú */
.app-sidebar :deep(.v-list-item) {
  margin-bottom: 2px;
  border-radius: 10px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.app-sidebar :deep(.v-list-item::before) {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 60%;
  background: linear-gradient(180deg, rgb(var(--v-theme-primary)) 0%, rgba(var(--v-theme-primary), 0.6) 100%);
  border-radius: 0 4px 4px 0;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-sidebar :deep(.v-list-item:hover) {
  background-color: rgba(var(--v-theme-primary), 0.06) !important;
}

.app-sidebar :deep(.v-list-item:hover .v-icon) {
  transform: scale(1.1);
}

.app-sidebar :deep(.v-list-item .v-icon) {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Item activo con indicador lateral */
.app-sidebar :deep(.v-list-item--active) {
  background: linear-gradient(90deg, rgba(var(--v-theme-primary), 0.12) 0%, rgba(var(--v-theme-primary), 0.04) 100%) !important;
}

.app-sidebar :deep(.v-list-item--active::before) {
  transform: translateY(-50%) scaleY(1);
}

.app-sidebar :deep(.v-list-item--active .v-list-item-title) {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600;
}

.app-sidebar :deep(.v-list-item--active .v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}

/* Grupos del menú */
.app-sidebar :deep(.v-list-group__items) {
  position: relative;
}

.app-sidebar :deep(.v-list-group__items::before) {
  content: '';
  position: absolute;
  left: 24px;
  top: 0;
  bottom: 8px;
  width: 1px;
  background: linear-gradient(180deg, rgba(var(--v-theme-primary), 0.2) 0%, transparent 100%);
}

/* Reducir padding izquierdo de items en submenús */
.app-sidebar:not(.v-navigation-drawer--rail) :deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 32px !important;
}

/* En rail mode: indentar iconos de submenús a la derecha (árbol) y ocultar títulos truncados */
.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item) {
  padding-inline: 0 !important;
  padding-left: 44px !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  width: 100% !important;
  min-height: 40px;
}

/* Sub-padre (v-list-group dentro de otro grupo, ej: Catálogos): más a la derecha que hijos simples */
.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items > .v-list-group > .v-list-item) {
  padding-left: 52px !important;
}

/* Segundo nivel de anidamiento (ej: Catálogos -> Productos/Servicios...) aún más a la derecha */
.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-group__items .v-list-item) {
  padding-left: 60px !important;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item-title) {
  display: none;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item__prepend) {
  display: inline-flex !important;
  margin: 0 !important;
  padding: 0 !important;
  opacity: 1 !important;
  width: auto !important;
  min-width: 0 !important;
  flex: 0 0 auto !important;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item__prepend .v-icon) {
  display: inline-flex !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item__content) {
  display: none !important;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items .v-list-item__append) {
  display: none;
}

.app-sidebar.v-navigation-drawer--rail :deep(.v-list-group__items::before) {
  display: none;
}

/* Footer del sidebar */
.app-sidebar :deep(.v-navigation-drawer__append) {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: linear-gradient(180deg, transparent 0%, rgba(var(--v-theme-surface-variant), 0.3) 100%);
}

/* Animación suave al expandir/colapsar grupos */
.app-sidebar :deep(.v-list-group__items) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover en el header del grupo */
.app-sidebar :deep(.v-list-group__header:hover) {
  background-color: rgba(var(--v-theme-primary), 0.06) !important;
}

/* Icono de expansión del grupo */
.app-sidebar :deep(.v-list-group__header .v-list-item__append .v-icon) {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Dark mode ajustes */
.v-theme--dark .app-sidebar,
.v-theme--darkTheme .app-sidebar {
  background: rgba(15, 23, 42, 0.7) !important;
  border-right-color: rgba(255, 255, 255, 0.08) !important;
}

.v-theme--dark .sidebar-header,
.v-theme--darkTheme .sidebar-header {
  background: linear-gradient(135deg, rgba(59, 80, 157, 0.08) 0%, rgba(59, 80, 157, 0.15) 100%);
  border-bottom-color: rgba(107, 127, 212, 0.2);
}

/* Estilos para el logo editable del SuperAdmin */
.cursor-pointer {
  cursor: pointer;
}

.logo-editable {
  position: relative;
  transition: all 0.2s ease;
}

.logo-editable:hover {
  transform: scale(1.05);
  box-shadow: 0 0 0 2px rgb(var(--v-theme-primary));
}

.logo-editable .logo-overlay {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.logo-editable:hover .logo-overlay {
  opacity: 1;
  background: rgba(0, 0, 0, 0.5) !important;
}

.logo-editable .edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.logo-editable:hover .edit-icon {
  opacity: 1;
}

/* =====================================================
   Logo animado (playbook seccion 2, adaptado a CSS):
   entrada scale+fade, hover scale, shimmer cada ~11s.
   ===================================================== */
.logo-animate {
  position: relative;
  overflow: hidden;
  animation: logo-in 0.5s cubic-bezier(0.32, 0.72, 0, 1) backwards;
  transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1);
}

/* Hover propio solo cuando NO es editable (SuperAdmin ya tiene su scale 1.05) */
.logo-animate:not(.logo-editable):hover {
  transform: scale(1.06);
}

.logo-animate::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    105deg,
    transparent 35%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 65%
  );
  transform: translateX(-200%);
  animation: logo-shimmer 11s ease-in-out infinite;
  pointer-events: none;
}

@keyframes logo-in {
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1); }
}

/* Barrido cada ~11s: cruza en los primeros ~3s, luego queda fuera de pantalla */
@keyframes logo-shimmer {
  0%   { transform: translateX(-200%); }
  27%  { transform: translateX(200%); }
  100% { transform: translateX(200%); }
}

/* Accesibilidad: sin selector universal (consistente con main.css) */
@media (prefers-reduced-motion: reduce) {
  .logo-animate,
  .logo-animate::after { animation: none !important; }
  .logo-animate:not(.logo-editable):hover { transform: none; }
}
</style>
